import { supabase } from '@/lib/supabase'
import type {
  AircraftTableRow,
  AircraftFormData,
  AircraftFilters,
} from '@/types/aircrafts'
import { applyRecordStatusFilter } from '@/lib/api/helpers/apply-record-status-filter'
import { restoreRecord } from '@/lib/api/helpers/restore-record'

/**
 * Fetch all aircraft with optional filtering
 * Includes joins to aircraft_types, clients, and locations
 */
export async function fetchAircraft(
  organizationId: string,
  filters?: AircraftFilters
): Promise<AircraftTableRow[]> {
  let query = supabase
    .from('aircraft')
    .select(`
      id,
      organization_id,
      tail_number,
      aircraft_type_id,
      manufacturer,
      model,
      owner_client_id,
      operator_client_id,
      owner_name,
      length,
      wingspan,
      height,
      tail_height,
      empty_weight,
      max_takeoff_weight,
      registration_country,
      serial_number,
      year_manufactured,
      annual_inspection_due,
      insurance_expiry,
      home_location_id,
      current_location_id,
      current_area_id,
      custom_model_file_path,
      custom_model_scale,
      status,
      notes,
      settings,
      created_at,
      updated_at,
      deleted_at,
      aircraft_types:aircraft_type_id (
        type_code,
        manufacturer,
        model,
        category,
        default_fuel_type
      ),
      home_location:home_location_id (
        name
      ),
      current_location:current_location_id (
        name
      ),
      current_area:current_area_id (
        name
      )
    `)
    .eq('organization_id', organizationId)

  query = applyRecordStatusFilter(query, filters?.recordStatus ?? 'active')

  query = query.order('tail_number')

  // Apply filters
  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status)
  }

  if (filters?.locationId) {
    query = query.or(`home_location_id.eq.${filters.locationId},current_location_id.eq.${filters.locationId}`)
  }

  if (filters?.search) {
    const search = filters.search.toLowerCase()
    query = query.or(
      `tail_number.ilike.%${search}%,owner_name.ilike.%${search}%,manufacturer.ilike.%${search}%,model.ilike.%${search}%`
    )
  }

  if (filters?.ownerClientId) {
    query = query.eq('owner_client_id', filters.ownerClientId)
  }

  const { data, error } = await query

  if (error) throw error

  // Transform joined data to flat structure
  return (data ?? []).map((row): AircraftTableRow => {
    const aircraftType = (row as any).aircraft_types as {
      type_code: string
      manufacturer: string | null
      model: string | null
      category: string | null
      default_fuel_type: string | null
    } | null
    const homeLocation = (row.home_location as unknown as { name: string } | null)
    const currentLocation = (row.current_location as unknown as { name: string } | null)
    const currentBuilding = (row.current_area as unknown as { name: string } | null)

    return {
      id: row.id,
      organization_id: row.organization_id,
      tail_number: row.tail_number,
      // Aircraft type
      aircraft_type_id: row.aircraft_type_id,
      aircraft_type_name: aircraftType?.type_code ?? null,
      aircraft_type_manufacturer: aircraftType?.manufacturer ?? null,
      aircraft_type_model: aircraftType?.model ?? null,
      aircraft_type_category: aircraftType?.category ?? null,
      aircraft_type_default_fuel_type: aircraftType?.default_fuel_type ?? null,
      // Manual overrides
      manufacturer: row.manufacturer,
      model: row.model,
      // Ownership
      owner_client_id: row.owner_client_id,
      owner_client_name: null,
      operator_client_id: row.operator_client_id,
      operator_client_name: null,
      owner_name: row.owner_name,
      // Dimensions
      length: row.length,
      wingspan: row.wingspan,
      height: row.height,
      tail_height: row.tail_height,
      empty_weight: row.empty_weight,
      max_takeoff_weight: row.max_takeoff_weight,
      // Registration
      registration_country: row.registration_country,
      serial_number: row.serial_number,
      year_manufactured: row.year_manufactured,
      // Compliance
      annual_inspection_due: row.annual_inspection_due,
      insurance_expiry: row.insurance_expiry,
      // Locations
      home_location_id: row.home_location_id,
      home_location_name: homeLocation?.name ?? null,
      current_location_id: row.current_location_id,
      current_location_name: currentLocation?.name ?? null,
      current_area_id: row.current_area_id,
      current_area_name: currentBuilding?.name ?? null,
      // Custom model
      custom_model_file_path: row.custom_model_file_path,
      custom_model_scale: row.custom_model_scale,
      // Status
      status: row.status,
      notes: row.notes,
      settings: row.settings as Record<string, unknown> | null,
      // Audit
      created_at: row.created_at,
      updated_at: row.updated_at,
      deleted_at: row.deleted_at,
    }
  })
}

/**
 * Fetch a single aircraft by ID with full details
 */
export async function fetchAircraftById(id: string): Promise<AircraftTableRow> {
  const { data, error } = await supabase
    .from('aircraft')
    .select(`
      id,
      organization_id,
      tail_number,
      aircraft_type_id,
      manufacturer,
      model,
      owner_client_id,
      operator_client_id,
      owner_name,
      length,
      wingspan,
      height,
      tail_height,
      empty_weight,
      max_takeoff_weight,
      registration_country,
      serial_number,
      year_manufactured,
      annual_inspection_due,
      insurance_expiry,
      home_location_id,
      current_location_id,
      current_area_id,
      custom_model_file_path,
      custom_model_scale,
      status,
      notes,
      settings,
      created_at,
      updated_at,
      deleted_at,
      aircraft_types:aircraft_type_id (
        type_code,
        manufacturer,
        model,
        category,
        default_fuel_type
      ),
      home_location:home_location_id (
        name
      ),
      current_location:current_location_id (
        name
      ),
      current_area:current_area_id (
        name
      )
    `)
    .eq('id', id)
    .single()

  if (error) throw error

  const aircraftType = (data as any).aircraft_types as {
    type_code: string
    manufacturer: string | null
    model: string | null
    category: string | null
    default_fuel_type: string | null
  } | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const owner = (data as any).owner as unknown as { name: string } | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const operator = (data as any).operator as unknown as { name: string } | null
  const homeLocation = (data.home_location as unknown as { name: string } | null)
  const currentLocation = (data.current_location as unknown as { name: string } | null)
  const currentBuilding = (data.current_area as unknown as { name: string } | null)

  return {
    id: data.id,
    organization_id: data.organization_id,
    tail_number: data.tail_number,
    aircraft_type_id: data.aircraft_type_id,
    aircraft_type_name: aircraftType?.type_code ?? null,
    aircraft_type_manufacturer: aircraftType?.manufacturer ?? null,
    aircraft_type_model: aircraftType?.model ?? null,
    aircraft_type_category: aircraftType?.category ?? null,
    aircraft_type_default_fuel_type: aircraftType?.default_fuel_type ?? null,
    manufacturer: data.manufacturer,
    model: data.model,
    owner_client_id: data.owner_client_id,
    owner_client_name: owner?.name ?? null,
    operator_client_id: data.operator_client_id,
    operator_client_name: operator?.name ?? null,
    owner_name: data.owner_name,
    length: data.length,
    wingspan: data.wingspan,
    height: data.height,
    tail_height: data.tail_height,
    empty_weight: data.empty_weight,
    max_takeoff_weight: data.max_takeoff_weight,
    registration_country: data.registration_country,
    serial_number: data.serial_number,
    year_manufactured: data.year_manufactured,
    annual_inspection_due: data.annual_inspection_due,
    insurance_expiry: data.insurance_expiry,
    home_location_id: data.home_location_id,
    home_location_name: homeLocation?.name ?? null,
    current_location_id: data.current_location_id,
    current_location_name: currentLocation?.name ?? null,
    current_area_id: data.current_area_id,
    current_area_name: currentBuilding?.name ?? null,
    custom_model_file_path: data.custom_model_file_path,
    custom_model_scale: data.custom_model_scale,
    status: data.status,
    notes: data.notes,
    settings: data.settings as Record<string, unknown> | null,
    created_at: data.created_at,
    updated_at: data.updated_at,
    deleted_at: data.deleted_at,
  }
}

/**
 * Create a new aircraft
 */
export async function createAircraft(
  organizationId: string,
  data: AircraftFormData
): Promise<AircraftTableRow> {
  const { data: created, error } = await supabase
    .from('aircraft')
    .insert({
      organization_id: organizationId,
      tail_number: data.tail_number.toUpperCase(),
      aircraft_type_id: data.aircraft_type_id ?? null,
      manufacturer: data.manufacturer ?? null,
      model: data.model ?? null,
      owner_client_id: data.owner_client_id ?? null,
      operator_client_id: data.operator_client_id ?? null,
      owner_name: data.owner_name ?? null,
      length: data.length ?? null,
      wingspan: data.wingspan ?? null,
      height: data.height ?? null,
      tail_height: data.tail_height ?? null,
      empty_weight: data.empty_weight ?? null,
      max_takeoff_weight: data.max_takeoff_weight ?? null,
      registration_country: data.registration_country ?? null,
      serial_number: data.serial_number ?? null,
      year_manufactured: data.year_manufactured ?? null,
      annual_inspection_due: data.annual_inspection_due ?? null,
      insurance_expiry: data.insurance_expiry ?? null,
      home_location_id: data.home_location_id ?? null,
      current_location_id: data.current_location_id ?? null,
      current_area_id: data.current_area_id ?? null,
      custom_model_file_path: data.custom_model_file_path ?? null,
      custom_model_scale: data.custom_model_scale ?? null,
      status: data.status,
      notes: data.notes ?? null,
    })
    .select()
    .single()

  if (error) throw error

  // Fetch with full joins for return
  return fetchAircraftById(created.id)
}

/**
 * Update an existing aircraft
 */
export async function updateAircraft(
  id: string,
  data: Partial<AircraftFormData>
): Promise<AircraftTableRow> {
  const updateData: Record<string, unknown> = {}

  // Only include fields that are explicitly provided
  if (data.tail_number !== undefined) updateData.tail_number = data.tail_number.toUpperCase()
  if (data.aircraft_type_id !== undefined) updateData.aircraft_type_id = data.aircraft_type_id
  if (data.manufacturer !== undefined) updateData.manufacturer = data.manufacturer
  if (data.model !== undefined) updateData.model = data.model
  if (data.owner_client_id !== undefined) updateData.owner_client_id = data.owner_client_id
  if (data.operator_client_id !== undefined) updateData.operator_client_id = data.operator_client_id
  if (data.owner_name !== undefined) updateData.owner_name = data.owner_name
  if (data.length !== undefined) updateData.length = data.length
  if (data.wingspan !== undefined) updateData.wingspan = data.wingspan
  if (data.height !== undefined) updateData.height = data.height
  if (data.tail_height !== undefined) updateData.tail_height = data.tail_height
  if (data.empty_weight !== undefined) updateData.empty_weight = data.empty_weight
  if (data.max_takeoff_weight !== undefined) updateData.max_takeoff_weight = data.max_takeoff_weight
  if (data.registration_country !== undefined) updateData.registration_country = data.registration_country
  if (data.serial_number !== undefined) updateData.serial_number = data.serial_number
  if (data.year_manufactured !== undefined) updateData.year_manufactured = data.year_manufactured
  if (data.annual_inspection_due !== undefined) updateData.annual_inspection_due = data.annual_inspection_due
  if (data.insurance_expiry !== undefined) updateData.insurance_expiry = data.insurance_expiry
  if (data.home_location_id !== undefined) updateData.home_location_id = data.home_location_id
  if (data.current_location_id !== undefined) updateData.current_location_id = data.current_location_id
  if (data.current_area_id !== undefined) updateData.current_area_id = data.current_area_id
  if (data.custom_model_file_path !== undefined) updateData.custom_model_file_path = data.custom_model_file_path
  if (data.custom_model_scale !== undefined) updateData.custom_model_scale = data.custom_model_scale
  if (data.status !== undefined) updateData.status = data.status
  if (data.notes !== undefined) updateData.notes = data.notes

  const { error } = await supabase
    .from('aircraft')
    .update(updateData)
    .eq('id', id)

  if (error) throw error

  // Fetch with full joins for return
  return fetchAircraftById(id)
}

/**
 * Soft delete an aircraft
 */
export async function deleteAircraft(id: string): Promise<void> {
  const { error } = await supabase
    .from('aircraft')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

/**
 * Check if a tail number already exists
 */
export async function checkTailNumberExists(
  organizationId: string,
  tailNumber: string,
  excludeId?: string
): Promise<boolean> {
  let query = supabase
    .from('aircraft')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('tail_number', tailNumber.toUpperCase())
    .is('deleted_at', null)

  if (excludeId) {
    query = query.neq('id', excludeId)
  }

  const { data, error } = await query.maybeSingle()

  if (error) throw error

  return !!data
}

/**
 * Restore a soft-deleted aircraft
 */
export async function restoreAircraft(id: string): Promise<void> {
  return restoreRecord('aircraft', id)
}
